import React, {
  useRef,
  useLayoutEffect,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { Button, Table } from "react-bootstrap";
import DataTable from "./DataTable";

export type ModalPosition = {
  top: number;
  left?: number;
  right?: number;
};
export type ModalMode = "table" | "details";
export interface DetailItem {
  label: string;
  value: ReactNode;
}

interface FloatingModalProps<T> {
  headers?: {
    key: string;
    label: string;
    sortable?: boolean;
    items?: any;
    style?: any;
    isNumeric?: boolean;
  }[];
  show: boolean;
  title: string;
  onClose: () => void;
  position?: ModalPosition | null;
  mode?: ModalMode;
  data?: any[];
  details?: DetailItem[];
  width?: string;
  maxHeight?: string;
}

const getAdjustedPosition = (
  position: ModalPosition | null,
  elementHeight: number
) => {
  if (!position) {
    return { top: "20%", left: "50%", transform: "translateX(-50%)" };
  }

  const viewportHeight = window.innerHeight;
  const viewportWidth = window.innerWidth;
  const modalWidth = viewportWidth * 0.5;

  let left =
    position.left ?? viewportWidth - (position.right ?? 0) - modalWidth;

  if (left + modalWidth > viewportWidth) left = viewportWidth - modalWidth - 10;
  if (left < 10) left = 10;

  const spaceBelow = viewportHeight - position.top;
  const top =
    spaceBelow < elementHeight
      ? position.top - elementHeight - 10
      : position.top;

  return { top, left, transform: "none" };
};

function FloatingTableModal<T>({
  show,
  title,
  onClose,
  position,
  mode = "table",
  data = [],
  headers = [],
  details = [],
  width = "50%",
  maxHeight = "45vh",
}: FloatingModalProps<T>) {
  const modalRef = useRef<HTMLDivElement>(null);
  const [adjustedPos, setAdjustedPos] = useState<any>(null);

  useLayoutEffect(() => {
    if (show && position && modalRef.current) {
      setAdjustedPos(
        getAdjustedPosition(position, modalRef.current.offsetHeight || 300)
      );
    }
  }, [show, position]);

  // Close on outside click
  useEffect(() => {
    if (!show) return;
    const handler = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [show, onClose]);

  // Close on outside scroll
  useEffect(() => {
    if (!show) return;
    const handler = (e: Event) => {
      if (modalRef.current?.contains(e.target as Node)) return;
      onClose();
    };
    window.addEventListener("scroll", handler, true);
    return () => window.removeEventListener("scroll", handler, true);
  }, [show, onClose]);

  if (!show) return null;

  return (
    <div
      ref={modalRef}
      style={{
        position: "fixed",
        width: mode === "details" ? "475px" : width,
        maxHeight,
        background: "#fff",
        border: "1px solid #ccc",
        borderRadius: "8px",
        boxShadow: "0 4px 10px rgba(0,0,0,0.2)",
        zIndex: 99,
        overflow: "hidden",
        ...(adjustedPos ?? {
          top: "20%",
          left: "50%",
          transform: "translateX(-50%)",
        }),
      }}
    >
      <div className="d-flex justify-content-between align-items-center px-3 py-1">
        <span className="btn-text fw-medium">{title}</span>
        <Button
          variant="light"
          size="sm"
          onClick={onClose}
          className="btn-text fw-bold px-0"
          style={{
            border: "none",
            background: "transparent",
            fontSize: "16px",
          }}
        >
          <span className="material-symbols-outlined">close</span>
        </Button>
      </div>

      <div
        style={{ maxHeight: "35vh", overflowY: "auto", padding: "0 10px 10px" }}
      >
        {mode === "details" ? (
          <div>
            {details.map((item, index) => (
              <div
                key={index}
                className="d-flex justify-content-start align-items-start py-1 border-bottom"
              >
                <span className="fw-medium text-muted fw-bold label-col">
                  {item.label}
                </span>
                <span className="text-start value-col">{item.value || "-"}</span>
              </div>
            ))}
          </div>
        ) : (
        <DataTable headers={headers} rows={data}/>
        )}
      </div>
    </div>
  );
}

export default FloatingTableModal;
